import { PlusCircle, MinusCircle, Search, Eraser, XCircle } from "lucide-react";
import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";

const iconBtnStyle = {
  cursor: "pointer",
  padding: "4px",
  borderRadius: "50%",
  transition: "0.2s",
  color: "#6b7280",
};

const iconHoverStyle = {
  transform: "scale(1.15)",
  color: "#2563eb",
};

const MultiColumnFilter = ({
  columns,
  data,
  onFilter,
  extra = [],
  output = "array",
  onCancel,
  fetchFunction,
  queryName,
}) => {
  const [filters, setFilters] = useState([
    {
      column: "",
      value: "",
      value2: "",
      dropDown: false,
      options: [],
      range: false,
      month: false,
      monthValue: "",
      yearValue: "",
      monthOrDate: "month",
    },
  ]);

  const [allRecords, setAllRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const getKey = (col) => col.selectorKey || col.id;
  // const fetchAllData = async () => {
  //   const apiCall = fetchFunction || queryName;

  //   if (!apiCall || typeof apiCall !== "function") {
  //     console.warn("No fetch function found");
  //     return;
  //   }

  //   setLoading(true);

  //   try {
  //     let page = 1;
  //     let per_page = 200;
  //     let combined = [];

  //     while (true) {
  //       const res = await apiCall({ page, per_page, searchTerm: "" });

  //       combined = [...combined, ...(res?.data || [])];

  //       if (!res?.total || combined.length >= res?.total) break;

  //       page++;
  //     }

  //     setAllRecords(combined);
  //   } catch (error) {
  //     console.error("Filter fetch error", error);
  //   }

  //   setLoading(false);
  // };

  const fetchAllData = async () => {
    if (!fetchFunction && !queryName) {
      console.warn("No fetchFunction or queryName provided");
      return;
    }

    setLoading(true);

    try {
      let page = 1;
      let per_page = 200;
      let combined = [];

      while (true) {
        // ✅ OLD PAYLOAD + NEW queryName
        const payload = {
          page,
          per_page,
          searchTerm: "",
          ...(queryName ? { queryName } : {}),
        };

        // ✅ ALWAYS call fetchFunction (OLD BEHAVIOR)
        const res = await fetchFunction(payload);

        combined = [...combined, ...(res?.data || [])];

        if (!res?.total || combined.length >= res.total) break;
        page++;
      }

      setAllRecords(combined);
    } catch (error) {
      console.error("Filter fetch error", error);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (fetchFunction || queryName) {
      fetchAllData();
    }
    // eslint-disable-next-line
  }, []);

  const finalData = allRecords.length > 0 ? allRecords : data || [];
  const handleColumnChange = (index, selectedColumn) => {
    const colObj = columns.find((col) => getKey(col) === selectedColumn);
    let options = [];

    if (colObj?.dropDown) {
      options = [
        ...new Set(finalData.map((row) => row[selectedColumn]).filter(Boolean)),
      ];
    }

    const newFilters = [...filters];
    newFilters[index] = {
      column: selectedColumn,
      value: "",
      value2: "",
      dropDown: colObj?.dropDown || false,
      options,
      range: colObj?.range || false,
      month: colObj?.month || false,
      monthValue: "",
      yearValue: "",
      monthOrDate: "month",
    };

    setFilters(newFilters);
  };

  const handleValueChange = (index, value, isSecond = false) => {
    const newFilters = [...filters];
    if (isSecond) newFilters[index].value2 = value;
    else newFilters[index].value = value;
    setFilters(newFilters);
  };

  const handleMonthYearChange = (index, type, value) => {
    const newFilters = [...filters];
    newFilters[index][type === "month" ? "monthValue" : "yearValue"] = value;
    setFilters(newFilters);
  };

  const handleMonthOrDateChange = (index, value) => {
    const newFilters = [...filters];
    newFilters[index].monthOrDate = value;
    setFilters(newFilters);
  };

  const addFilterRow = () => {
    setFilters([
      ...filters,
      {
        column: "",
        value: "",
        value2: "",
        dropDown: false,
        options: [],
        range: false,
        month: false,
        monthValue: "",
        yearValue: "",
        monthOrDate: "month",
      },
    ]);
  };

  const removeFilterRow = (index) => {
    const newFilters = filters.filter((_, i) => i !== index);
    setFilters(
      newFilters.length > 0
        ? newFilters
        : [
            {
              column: "",
              value: "",
              value2: "",
              dropDown: false,
              options: [],
              range: false,
              month: false,
              monthValue: "",
              yearValue: "",
              monthOrDate: "month",
            },
          ]
    );
  };

  const buildAppliedFiltersArray = () =>
    filters
      .filter(
        (f) =>
          f.column &&
          (f.value ||
            f.value2 ||
            (f.month &&
              f.monthOrDate === "month" &&
              f.monthValue &&
              f.yearValue))
      )
      .map((f) => {
        if (f.month && f.monthOrDate === "month") {
          return {
            [f.column]: {
              month: parseInt(f.monthValue),
              year: parseInt(f.yearValue),
            },
          };
        }

        if (f.range || (f.month && f.monthOrDate === "date")) {
          return {
            [f.column]: {
              $gte: f.value || "",
              $lte: f.value2 || "",
            },
          };
        }

        return { [f.column]: f.value };
      });

  const handleApplyFilters = () => {
    const appliedFilters = buildAppliedFiltersArray();

    let finalResult;

    if (output === "array") {
      finalResult = appliedFilters;
    } else if (output === "and") {
      finalResult = appliedFilters.length > 0 ? { $and: appliedFilters } : {};
    } else {
      const hasRangeOrMonth = filters.some(
        (f) => f.range || (f.month && f.monthOrDate === "month")
      );
      finalResult =
        hasRangeOrMonth && appliedFilters.length > 0
          ? { $and: appliedFilters }
          : appliedFilters;
    }

    onFilter(finalResult);
  };

  const handleClearFilters = () => {
    setFilters([
      {
        column: "",
        value: "",
        value2: "",
        dropDown: false,
        options: [],
        range: false,
        month: false,
        monthValue: "",
        yearValue: "",
        monthOrDate: "month",
      },
    ]);

    onFilter(output === "array" ? [] : {});
  };

  const months = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];

  const years = Array.from(
    { length: 20 },
    (_, i) => new Date().getFullYear() - i
  );
  return (
    <div
      style={{
        border: "1px dashed #ccc",
        padding: "15px",
        borderRadius: "6px",
        marginTop: "10px",
        marginBottom: "10px",
        background: "#f7faff",
      }}
    >
      {loading && (
        <div className="text-center py-2">
          <small>Loading filter data…</small>
        </div>
      )}

      {filters.map((filter, index) => (
        <Row key={index} className="align-items-center mb-2">
          <Col xs={4} className="pe-3">
            <Form.Label>Action</Form.Label>
            <Form.Select
              size="sm"
              value={filter.column}
              onChange={(e) => handleColumnChange(index, e.target.value)}
            >
              <option value="">Select Action</option>
              {columns.map((col) => {
                const key = getKey(col);
                return (
                  <option key={key} value={key}>
                    {col.name}
                  </option>
                );
              })}
            </Form.Select>
          </Col>

          <Col xs={4} className="px-3">
            {filter.column ? (
              <>
                <Form.Label>Value</Form.Label>

                {/* MONTH / DATE */}
                {filter.month ? (
                  <>
                    <Form.Select
                      size="sm"
                      value={filter.monthOrDate}
                      onChange={(e) =>
                        handleMonthOrDateChange(index, e.target.value)
                      }
                      className="mb-1"
                    >
                      <option value="month">Month-wise</option>
                      <option value="date">Date-wise</option>
                    </Form.Select>

                    {filter.monthOrDate === "month" ? (
                      <Row className="g-1">
                        <Col>
                          <Form.Select
                            size="sm"
                            value={filter.monthValue}
                            onChange={(e) =>
                              handleMonthYearChange(
                                index,
                                "month",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Month</option>
                            {months.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>

                        <Col>
                          <Form.Select
                            size="sm"
                            value={filter.yearValue}
                            onChange={(e) =>
                              handleMonthYearChange(
                                index,
                                "year",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Year</option>
                            {years.map((y) => (
                              <option key={y} value={y}>
                                {y}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    ) : (
                      <Row className="g-1">
                        <Col>
                          <Form.Control
                            size="sm"
                            type="date"
                            value={filter.value}
                            onChange={(e) =>
                              handleValueChange(index, e.target.value)
                            }
                          />
                        </Col>
                        <Col>
                          <Form.Control
                            size="sm"
                            type="date"
                            value={filter.value2}
                            onChange={(e) =>
                              handleValueChange(index, e.target.value, true)
                            }
                          />
                        </Col>
                      </Row>
                    )}
                  </>
                ) : filter.range ? (
                  <Row className="g-1">
                    <Col>
                      <Form.Control
                        size="sm"
                        placeholder="From"
                        value={filter.value}
                        onChange={(e) =>
                          handleValueChange(index, e.target.value)
                        }
                      />
                    </Col>
                    <Col>
                      <Form.Control
                        size="sm"
                        placeholder="To"
                        value={filter.value2}
                        onChange={(e) =>
                          handleValueChange(index, e.target.value, true)
                        }
                      />
                    </Col>
                  </Row>
                ) : filter.dropDown ? (
                  <Form.Select
                    size="sm"
                    value={filter.value}
                    onChange={(e) => handleValueChange(index, e.target.value)}
                  >
                    <option value="">Select Value</option>
                    {filter.options.map((val) => (
                      <option key={val} value={val}>
                        {val}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    size="sm"
                    placeholder="Enter value"
                    value={filter.value}
                    onChange={(e) => handleValueChange(index, e.target.value)}
                  />
                )}
              </>
            ) : (
              <div style={{ height: "31px" }} />
            )}
          </Col>

          {/* Add / Remove */}
          <Col xs={4} className="ps-4 d-flex align-items-center">
            <div className="d-flex gap-3 align-items-center">
              {filter.column && (
                <div
                  title="Add Filter"
                  style={iconBtnStyle}
                  onClick={addFilterRow}
                  onMouseEnter={(e) =>
                    Object.assign(e.currentTarget.style, iconHoverStyle)
                  }
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.color = "#6b7280";
                  }}
                >
                  <PlusCircle size={28} />
                </div>
              )}

              {filters.length > 1 && (
                <div
                  title="Remove Filter"
                  style={iconBtnStyle}
                  onClick={() => removeFilterRow(index)}
                  onMouseEnter={(e) =>
                    Object.assign(e.currentTarget.style, iconHoverStyle)
                  }
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "scale(1)";
                    e.currentTarget.style.color = "#6b7280";
                  }}
                >
                  <MinusCircle size={28} />
                </div>
              )}
            </div>
          </Col>
        </Row>
      ))}

      {/* Action Buttons */}
      <Row className="mt-4">
        <Col className="d-flex gap-4 align-items-center">
          {/* Search */}
          <div
            title="Search"
            style={iconBtnStyle}
            onClick={handleApplyFilters}
            onMouseEnter={(e) =>
              Object.assign(e.currentTarget.style, iconHoverStyle)
            }
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.color = "#6b7280";
            }}
          >
            <Search size={30} />
          </div>

          {/* Clear */}
          <div
            title="Clear Filters"
            style={iconBtnStyle}
            onClick={handleClearFilters}
            onMouseEnter={(e) =>
              Object.assign(e.currentTarget.style, iconHoverStyle)
            }
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.color = "#6b7280";
            }}
          >
            <Eraser size={30} />
          </div>

          {/* Cancel */}
          <div
            title="Cancel"
            style={iconBtnStyle}
            onClick={() => {
              handleClearFilters();
              onCancel();
            }}
            onMouseEnter={(e) =>
              Object.assign(e.currentTarget.style, iconHoverStyle)
            }
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.color = "#6b7280";
            }}
          >
            <XCircle size={30} />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default MultiColumnFilter;
